import type {
    Quote,
    QuoteItem,
    QuoteOrderLine,
    QuoteOrderTranche,
    QuoteStatus,
    ServiceItem,
} from '../types';
import { activeQuoteItems, calculateQuoteTotals, mapQuoteItemsToServiceItems, round2 } from './quoteLogic';
import { hatBestandteile } from './bestandteile/bestandteilLogic';
import { gueltigesKontingent } from './bestandteile/kontingentKopie';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TEILBEAUFTRAGUNG — Auftragsteile eines Angebots
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Der Fall: der Kunde beauftragt aus einem Angebot erst einen Teil (z. B. die
 * Planungsphase und 30 von 77 Begehungen) und den Rest Monate später — oder
 * nie. Bisher kannte das Angebot nur „angenommen" als Endzustand: der Rest
 * liess sich weder nachbeauftragen noch in der Statistik als offen zeigen.
 *
 * Grundsatz: Jeder Auftragsteil ist ein Eintrag in `quotes.order_tranches`,
 * der nur angehängt wird. Beauftragte, entfallene und offene Mengen sowie alle
 * Summen werden HIER aus diesen Einträgen abgeleitet — es gibt kein zweites
 * gespeichertes Mengenfeld, das auseinanderlaufen könnte.
 *
 * Verbindung zum Projekt: die Leistungsposition im Projekt-LV trägt dieselbe
 * id wie die Angebotsposition (quoteLogic.mapQuoteItemsToServiceItems). Ein
 * späterer Teil erhöht dort die Vertragsmenge oder hängt die Position an.
 *
 * NEP-Positionen (Eventualpositionen) sind nie Teil eines Auftragsteils: sie
 * stehen in keiner Angebotssumme und gehen wie bisher mit dem ERSTEN Teil als
 * NEP ins Projekt. Gestrichene Positionen gehören nicht zum Angebot.
 *
 * Rein: kein Netz, keine Uhr (Zeitpunkte und ids kommen herein).
 */

/** Mengen werden auf drei Nachkommastellen geführt (wie das Buchungsjournal). */
const qty3 = (n: number): number => Math.round((n + Number.EPSILON) * 1000) / 1000;
const EPS = 1e-6;

const day = (iso?: string | null): string => (iso ? String(iso).slice(0, 10) : '');

const num = (v: unknown): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

/** Positionen, die beauftragt werden können: nicht gestrichen und keine NEP. */
export const orderableQuoteItems = <T extends Pick<QuoteItem, 'excluded' | 'nep'>>(items: T[] | null | undefined): T[] =>
    activeQuoteItems(items || []).filter((it) => !it.nep);

/** Ganz oder teilweise beauftragt — der Angebotsstatus, an dem ein Projekt hängt. */
export const isQuoteOrdered = (status: QuoteStatus | null | undefined): boolean =>
    status === 'accepted' || status === 'partial';

/**
 * Altbestand: angenommen, bevor es Auftragsteile gab. Das Angebot gilt als
 * VOLL beauftragt — ein Teil mit allen Positionen und der gespeicherten
 * Angebotssumme (dieselbe Zahl, die Statistik und Liste bisher zeigten).
 */
const legacyFullTranche = (q: Quote): QuoteOrderTranche => {
    const at = q.acceptedAt || q.updatedAt || q.quoteDate || '';
    return {
        id: `legacy-${q.id}`,
        kind: 'order',
        seq: 1,
        at,
        orderDate: day(at),
        byName: '',
        lines: orderableQuoteItems(q.items).map((it) => ({ itemId: it.id, quantity: num(it.quantity) })),
        netAmount: num(q.netTotal),
        appliedAt: at,
    };
};

/**
 * Die Auftragsteile, mit denen gerechnet wird, nach laufender Nummer.
 * Angenommen ohne gespeicherte Teile = Altbestand, ein voller Teil.
 */
export const effectiveTranches = (q: Quote): QuoteOrderTranche[] => {
    const stored = Array.isArray(q.orderTranches) ? q.orderTranches.filter(Boolean) : [];
    if (stored.length > 0) return [...stored].sort((a, b) => (a.seq || 0) - (b.seq || 0));
    if (q.status === 'accepted') return [legacyFullTranche(q)];
    return [];
};

/** Mengenstand einer Angebotsposition. */
export interface ItemOrderState {
    itemId: string;
    offered: number;
    ordered: number;
    declined: number;
    open: number;
}

/** Mengenstand je beauftragbarer Position (Schlüssel: QuoteItem.id). */
export const itemOrderStates = (q: Quote): Map<string, ItemOrderState> => {
    const ordered = new Map<string, number>();
    const declined = new Map<string, number>();
    for (const t of effectiveTranches(q)) {
        const target = t.kind === 'decline' ? declined : ordered;
        for (const l of t.lines || []) target.set(l.itemId, (target.get(l.itemId) || 0) + num(l.quantity));
    }
    const out = new Map<string, ItemOrderState>();
    for (const it of orderableQuoteItems(q.items)) {
        const offered = num(it.quantity);
        const o = qty3(ordered.get(it.id) || 0);
        const d = qty3(declined.get(it.id) || 0);
        out.set(it.id, { itemId: it.id, offered, ordered: o, declined: d, open: Math.max(0, qty3(offered - o - d)) });
    }
    return out;
};

/** Summen eines Angebots nach Auftragsteilen. */
export interface QuoteOrderSummary {
    /** Netto beauftragt (Summe der Teile `order`). */
    orderedNet: number;
    /** Netto entfallen (Summe der Teile `decline`). */
    declinedNet: number;
    /** Netto noch offen — nur bei `partial` größer 0. */
    openNet: number;
    /** Mindestens ein Teil beauftragt. */
    hasOrder: boolean;
    /** Teile, die noch nicht im Projekt-LV stehen. */
    pendingCount: number;
    /** Datum des ersten Auftragsteils (yyyy-mm-dd), '' ohne Auftrag. */
    firstOrderDate: string;
}

export const quoteOrderSummary = (q: Quote): QuoteOrderSummary => {
    const tranches = effectiveTranches(q);
    let orderedNet = 0;
    let declinedNet = 0;
    let pendingCount = 0;
    let firstOrderDate = '';
    for (const t of tranches) {
        if (t.kind === 'decline') {
            declinedNet += num(t.netAmount);
            continue;
        }
        orderedNet += num(t.netAmount);
        if (!t.appliedAt) pendingCount += 1;
        const d = t.orderDate || day(t.at);
        if (d && (!firstOrderDate || d < firstOrderDate)) firstOrderDate = d;
    }
    orderedNet = round2(orderedNet);
    declinedNet = round2(declinedNet);
    const openNet = q.status === 'partial'
        ? Math.max(0, round2(num(q.netTotal) - orderedNet - declinedNet))
        : 0;
    return { orderedNet, declinedNet, openNet, hasOrder: orderedNet > 0 || tranches.some((t) => t.kind === 'order'), pendingCount, firstOrderDate };
};

/**
 * Anteil einer Position, der beauftragt ist (0…1) — für die Aufschlüsselung
 * des gewonnenen Volumens nach Leistungsart. Nicht beauftragte Angebote: 0.
 */
export const orderedShareOfItem = (q: Quote, itemId: string): number => {
    if (!isQuoteOrdered(q.status)) return 0;
    const s = itemOrderStates(q).get(itemId);
    if (!s || s.offered <= 0) return 0;
    return Math.min(1, s.ordered / s.offered);
};

/** Bleibt nach diesen Zeilen noch etwas offen? */
const leavesOpen = (q: Quote, lines: QuoteOrderLine[]): boolean => {
    const add = new Map<string, number>();
    for (const l of lines) add.set(l.itemId, (add.get(l.itemId) || 0) + num(l.quantity));
    for (const s of itemOrderStates(q).values()) {
        if (s.open - (add.get(s.itemId) || 0) > EPS) return true;
    }
    return false;
};

/**
 * Nettobetrag eines neuen Teils — dieselbe Rechnung wie die Angebotssumme
 * (calculateQuoteTotals: Zeilenbetrag, Gesamtrabatt, kaufmännisch gerundet),
 * nur über die Mengen dieses Teils.
 *
 * SCHLUSSTEIL: lässt der Teil nichts mehr offen, ist sein Betrag die
 * Angebotssumme minus alle früheren Teile. Sonst summierten sich die Teile
 * durch Rundung um Cents neben die Angebotssumme.
 */
export const computeTrancheNet = (q: Quote, lines: QuoteOrderLine[]): number => {
    const qtyById = new Map<string, number>();
    for (const l of lines) if (num(l.quantity) > 0) qtyById.set(l.itemId, (qtyById.get(l.itemId) || 0) + num(l.quantity));
    if (!leavesOpen(q, lines)) {
        const total = calculateQuoteTotals(q.items || [], q.discountPercent || 0, 0).netTotal;
        const prior = effectiveTranches(q).reduce((sum, t) => sum + num(t.netAmount), 0);
        return Math.max(0, round2(total - prior));
    }
    const items = orderableQuoteItems(q.items)
        .filter((it) => qtyById.has(it.id))
        .map((it) => ({ ...it, quantity: qtyById.get(it.id)! }));
    return calculateQuoteTotals(items, q.discountPercent || 0, 0).netTotal;
};

/** Alle offenen Mengen — Vorbelegung „vollständig" bzw. „Rest beauftragen". */
export const openOrderLines = (q: Quote): QuoteOrderLine[] =>
    [...itemOrderStates(q).values()]
        .filter((s) => s.open > EPS)
        .map((s) => ({ itemId: s.itemId, quantity: s.open }));

/** Zeilen mit Menge > 0, Mengen auf drei Stellen. */
export const normalizeOrderLines = (lines: QuoteOrderLine[] | null | undefined): QuoteOrderLine[] =>
    (lines || [])
        .map((l) => ({ itemId: String(l.itemId), quantity: qty3(num(l.quantity)) }))
        .filter((l) => l.quantity > 0);

/**
 * Prüft die Zeilen eines neuen Teils gegen den offenen Stand. Liefert die
 * Fehler als Klartext; leer = gültig.
 */
export const validateOrderLines = (q: Quote, lines: QuoteOrderLine[]): string[] => {
    const errors: string[] = [];
    const states = itemOrderStates(q);
    const items = new Map(orderableQuoteItems(q.items).map((it) => [it.id, it]));
    const seen = new Set<string>();
    const label = (id: string) => {
        const it = items.get(id);
        return it ? `${it.lvPosition || it.posNumber || ''} ${it.title}`.trim() : id;
    };
    for (const l of lines) {
        const qty = num(l.quantity);
        if (seen.has(l.itemId)) errors.push(`${label(l.itemId)}: Position doppelt gewählt.`);
        seen.add(l.itemId);
        const s = states.get(l.itemId);
        if (!s) { errors.push(`${label(l.itemId)}: gehört nicht (mehr) zu den beauftragbaren Positionen.`); continue; }
        if (!(qty > 0)) { errors.push(`${label(l.itemId)}: Menge muss größer als 0 sein.`); continue; }
        if (qty - s.open > EPS) errors.push(`${label(l.itemId)}: höchstens ${formatQty(s.open)} offen, gewählt ${formatQty(qty)}.`);
    }
    if (lines.length === 0) errors.push('Keine Position mit Menge gewählt.');
    return errors;
};

/** Status nach einem neuen Teil: alles entschieden → accepted, sonst partial. */
export const statusAfterLines = (q: Quote, lines: QuoteOrderLine[]): QuoteStatus =>
    leavesOpen(q, lines) ? 'partial' : 'accepted';

export interface BuildTrancheInput {
    id: string;
    at: string;
    kind: QuoteOrderTranche['kind'];
    lines: QuoteOrderLine[];
    orderDate?: string;
    by?: string;
    byName: string;
    customerRef?: string;
    note?: string;
}

/**
 * Neuer Teil für dieses Angebot (laufende Nummer, eingefrorener Betrag).
 * `decline` gilt sofort als erledigt (kein Projekt-LV dahinter).
 */
export const buildTranche = (q: Quote, input: BuildTrancheInput): QuoteOrderTranche => {
    const lines = normalizeOrderLines(input.lines);
    const stored = Array.isArray(q.orderTranches) ? q.orderTranches : [];
    const seq = stored.reduce((m, t) => Math.max(m, num(t.seq)), 0) + 1;
    const ref = input.customerRef?.trim();
    const note = input.note?.trim();
    return {
        id: input.id,
        kind: input.kind,
        seq,
        at: input.at,
        orderDate: day(input.orderDate) || day(input.at),
        ...(input.by ? { by: input.by } : {}),
        byName: input.byName,
        ...(ref ? { customerRef: ref } : {}),
        lines,
        netAmount: computeTrancheNet(q, lines),
        ...(note ? { note } : {}),
        ...(input.kind === 'decline' ? { appliedAt: input.at } : {}),
    };
};

/** Setzt `appliedAt` an genau einem Teil — nur, wenn es noch fehlt. */
export const markTrancheApplied = (
    tranches: QuoteOrderTranche[] | null | undefined,
    trancheId: string,
    at: string,
): QuoteOrderTranche[] =>
    (tranches || []).map((t) => (t.id === trancheId && !t.appliedAt ? { ...t, appliedAt: at } : t));

/**
 * Kennung eines Teils im Vermerk des Auftragswerts. Ein zweiter Eintragelauf
 * (Erneut eintragen) erkennt daran, dass der Teil im Wert schon steckt.
 */
export const trancheOrderValueMarker = (t: Pick<QuoteOrderTranche, 'id'>): string => `#${String(t.id).slice(0, 8)}`;

/**
 * Alle Kennungen, die im Vermerk eines Auftragswerts stehen. Der Vermerk
 * fuehrt sie KUMULIERT mit („Eingerechnet: #… #…"): ueberschriebe Teil 3 nur
 * seine eigene Kennung, sähe ein spaeteres „Erneut eintragen" von Teil 2 den
 * schon eingerechneten Teil nicht mehr und addierte ihn ein zweites Mal.
 */
export const orderValueMarkers = (note: string | null | undefined): string[] =>
    [...new Set(String(note ?? '').match(/#[0-9a-zA-Z-]{8}/g) || [])];

/** Steckt dieser Teil laut Vermerk schon im Auftragswert? */
export const isTrancheInOrderValue = (note: string | null | undefined, t: Pick<QuoteOrderTranche, 'id'>): boolean =>
    orderValueMarkers(note).includes(trancheOrderValueMarker(t));

/** Vermerk zum Auftragswert nach einem Teil: Text + alle bisherigen Kennungen + diese. */
export const orderValueNoteFor = (
    previousNote: string | null | undefined,
    text: string,
    t: Pick<QuoteOrderTranche, 'id'>,
): string => {
    const markers = orderValueMarkers(previousNote);
    const mine = trancheOrderValueMarker(t);
    if (!markers.includes(mine)) markers.push(mine);
    return `${text} Eingerechnet: ${markers.join(' ')}`;
};

/**
 * Welcher Auftragswert geschrieben wird. Der Dialog schickt den NEUEN
 * Gesamtwert, gerechnet aus dem Bestand, den er beim Öffnen sah (`baseCents`).
 * Hat seitdem ein anderer Teil oder Nachtrag den Wert geändert (`freshCents`
 * ≠ Basis), nähme der veraltete Gesamtwert dessen Anteil wieder heraus
 * (10.000 Bestand, A +1.000 → 11.000, B +2.000 schickte 12.000 statt 13.000).
 * Dann: frischer Wert + (neu − Basis).
 *
 * Ohne Basis wie bisher (`newCents`). Ebenso, wenn Basis oder frischer Wert
 * fehlt: ohne bisherigen Wert hat der Nutzer einen Gesamtwert eingetragen,
 * keinen Zuwachs — ihn auf einen fremden Wert zu addieren zählte doppelt.
 */
export const orderValueCentsToWrite = (
    newCents: number,
    baseCents: number | null | undefined,
    freshCents: number | null | undefined,
): number => {
    if (baseCents == null || freshCents == null || !Number.isFinite(baseCents) || !Number.isFinite(freshCents)) return newCents;
    if (Math.round(freshCents) === Math.round(baseCents)) return newCents;
    return Math.max(0, Math.round(freshCents + (newCents - baseCents)));
};

/** Beauftragte Teile, die noch nicht im Projekt-LV stehen. */
export const pendingOrderTranches = (q: Quote): QuoteOrderTranche[] =>
    (Array.isArray(q.orderTranches) ? q.orderTranches : []).filter((t) => t.kind === 'order' && !t.appliedAt);

/**
 * Angebotspositionen, aus denen das Projekt beim ERSTEN Teil entsteht: jede
 * beauftragte Position mit der beauftragten Menge, NEP wie bisher mit, alles
 * andere nicht. Reihenfolge wie im Angebot.
 */
export const projectItemsForLines = (items: QuoteItem[], lines: QuoteOrderLine[]): QuoteItem[] => {
    const qtyById = new Map<string, number>();
    for (const l of normalizeOrderLines(lines)) qtyById.set(l.itemId, (qtyById.get(l.itemId) || 0) + l.quantity);
    return activeQuoteItems(items || []).flatMap((it) => {
        if (it.nep) return [it];
        const q = qtyById.get(it.id);
        return q && q > 0 ? [{ ...it, quantity: q }] : [];
    });
};

/** Ein Schritt im Projekt-LV für einen späteren Teil. */
export interface LvPlanStep {
    itemId: string;
    kind: 'increase' | 'append';
    lvPosition: string;
    title: string;
    unit: string;
    /** Vertragsmenge vorher (0 bei append) und nachher. */
    from: number;
    to: number;
    add: number;
    /** Einzelpreis im Projekt weicht vom Angebot ab (nur Hinweis, die Menge zählt trotzdem). */
    priceDiffers?: { project: number; quote: number };
    /**
     * Paketposition: die Bestandteile des Angebots werden beim Anhängen NICHT
     * übernommen (Menge dieses Teils nicht 1, E5). Nur Hinweis — die neue
     * Position ist dann eine gewöhnliche.
     */
    bestandteileHinweis?: string;
    /**
     * Paketposition: dieser Schritt würde sie über Menge 1 erhöhen — gesperrt
     * (steht auch in `LvPlan.packageBlocks`). Die Position bleibt im Plan
     * unverändert.
     */
    packageBlock?: string;
    /**
     * Nur mit Zuordnung (LvPlanOptions, Nachtrag): bei `increase` die
     * Projektposition, deren Vertragsmenge steigt; bei `append` die id der
     * neuen Position (meist = itemId, siehe `serviceIdByItemId`).
     */
    serviceId?: string;
    /**
     * Nur mit `increaseBlocker` (Nachtrag): eine Erhöhung war vorgesehen, sie
     * ist nicht zulässig — angelegt wird eine neue Position, mit Grund.
     */
    forcedAppendReason?: string;
    /** NEP-Position des Angebots, als Eventualposition (is_optional) angehängt. */
    nep?: boolean;
}

export interface LvPlan {
    services: ServiceItem[];
    steps: LvPlanStep[];
    /** ids der neu angehängten Positionen (Monatspauschale: ja/nein-Entscheidung). */
    newServiceIds: string[];
    /** Zeilen ohne Angebotsposition — dürfen nicht vorkommen, sperren das Eintragen. */
    missing: string[];
    /**
     * Zeilen, die eine Paketposition über Menge 1 erhöhen würden — sperren das
     * Eintragen wie `missing`, mit Grund für den Dialog.
     */
    packageBlocks: { itemId: string; message: string }[];
    /**
     * Nur mit Zuordnung: Angebotsposition → Projektposition, auf der JEDE
     * Zeile gelandet ist (erhöht oder angehängt, auch bei gleicher id; NEP
     * nicht). Geht an den Auftragsteil und ist dort verbindlich: spätere
     * Teile und „Erneut eintragen" prüfen nur noch diese Position (und die
     * gleiche id), keinen Vorschlag. Ohne Optionen leer.
     */
    serviceIdByItemId: Record<string, string>;
    /** Nur mit Zuordnung: die zugeordnete Projektposition fehlt — sperrt wie `missing`. */
    missingTargets: { itemId: string; serviceId: string }[];
}

/**
 * Zuordnung für Auftragsteile, deren Positionen NICHT dieselbe id wie die
 * Projektpositionen tragen (Nachtragsangebot, quoteSupplementLogic). Ohne
 * Optionen rechnet planTrancheForProjectLv wie bisher: gleiche id → erhöhen,
 * sonst anhängen.
 */
export interface LvPlanOptions {
    /**
     * Zuordnung früherer Teile (bzw. des Teils selbst beim erneuten
     * Eintragen). VERBINDLICH: hat eine Position hier einen Eintrag, werden
     * nur die gleiche id und dieser Eintrag geprüft — die Dialog-Zuordnung
     * nicht mehr.
     */
    priorServiceIdByItemId?: Record<string, string>;
    /** Zuordnung aus dem Dialog bzw. Vorschlag: gilt NACH der gleichen id, nur ohne frühere Zuordnung. */
    mappedServiceIdByItemId?: Record<string, string>;
    /**
     * Warum eine Erhöhung nicht zulässig ist (null = zulässig). Gesetzt, wird
     * eine gesperrte Erhöhung zur NEUEN Position mit `forcedAppendReason` —
     * statt Preis-Hinweis und Paketsperre der Teilbeauftragung.
     */
    increaseBlocker?: (svc: ServiceItem, item: QuoteItem, add: number) => string | null;
    /** LV-Nr. der n-ten in diesem Plan angehängten Position (0-basiert). */
    appendLvPosition?: (index: number) => string;
    /** NEP-Positionen des Angebots als Eventualposition anhängen (erster Teil). */
    includeNep?: boolean;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * n-te abgeleitete Positions-id einer Angebotsposition (n ≥ 2): für den
 * seltenen Fall, dass die Position ein zweites Mal NEU angelegt werden muss,
 * ihre eigene id im Projekt aber schon vergeben ist (z. B. zweites Paket aus
 * demselben Nachtrag). Bei einer uuid bleibt es eine gültige uuid — die
 * Spalte `project_services.id` nimmt nichts anderes —, und sie bleibt als
 * „eigene" id der Angebotsposition erkennbar (derivedIndex).
 */
const derivedServiceId = (itemId: string, n: number): string =>
    UUID_RE.test(itemId) ? `${itemId.slice(0, 24)}${n.toString(16).padStart(12, '0')}` : `${itemId}~${n}`;

/** Laufende Nummer, wenn `sid` eine abgeleitete id von `itemId` ist — sonst null. */
const derivedIndex = (itemId: string, sid: string): number | null => {
    if (sid === itemId) return null;
    if (UUID_RE.test(itemId)) {
        if (!UUID_RE.test(sid) || sid.slice(0, 24).toLowerCase() !== itemId.slice(0, 24).toLowerCase()) return null;
        const n = parseInt(sid.slice(24), 16);
        return n >= 2 && n < 1_000_000 ? n : null;
    }
    if (!sid.startsWith(`${itemId}~`)) return null;
    const n = Number(sid.slice(itemId.length + 1));
    return Number.isInteger(n) && n >= 2 ? n : null;
};

/**
 * Sperrgrund, wenn ein Auftragsteil eine Paketposition (Bestandteile) über
 * Menge 1 erhöhen würde.
 *
 * Bestandteile gibt es nur bei Menge 1 (E5). Eine Paketposition mit Menge 2
 * waere weder automatisch noch von Hand buchbar (die Buchung prueft die
 * Definition und meldet `menge_ungueltig`), die direkten Buchungswege sind
 * fuer Paketpositionen gesperrt — die zweite Menge liesse sich nie abrechnen.
 * Deshalb kein Hinweis, sondern eine Sperre: ein zweites Paket ist eine eigene
 * Position, oder ein Admin entfernt vorher die Bestandteile.
 */
export const paketErhoehungGesperrt = (
    svc: Pick<ServiceItem, 'bestandteile' | 'title' | 'lvPosition'>,
    to: number,
): string | null => {
    if (!hatBestandteile(svc) || Math.abs(to - 1) <= 0.0005) return null;
    const name = svc.title || svc.lvPosition || 'Position';
    return `Paketposition ‚${name}‘ kann nicht über Menge 1 erhöht werden — ein zweites Paket bitte als eigene Position beauftragen bzw. die Bestandteile vorher von einem Admin entfernen lassen.`;
};

/**
 * Was ein späterer Teil im Projekt-LV ändert: vorhandene Position (gleiche id)
 * → Vertragsmenge erhöhen; fehlende → anhängen, so wie die Annahme sie
 * angelegt hätte. Gebuchte, abgerechnete und verzichtete Mengen bleiben
 * unberührt — es wächst nur die Vertragsmenge.
 *
 * Mit `opts` (Nachtragsangebot, quoteSupplementLogic) sucht der Plan die
 * Zielposition je Zeile in dieser Reihenfolge: gleiche id (eigene Position
 * aus einem früheren Teil), dann die Zuordnung früherer Teile — oder, NUR
 * wenn es keine gibt, die des Dialogs. Die erste vorhandene, zulässige
 * Position wird erhöht. Fehlt eine zugeordnete FREMDE Position →
 * `missingTargets` (sperrt). Ist keine Erhöhung zulässig (`increaseBlocker`),
 * wird angehängt — mit Grund. Wo jede Zeile landet, steht danach in
 * `serviceIdByItemId`.
 */
export const planTrancheForProjectLv = (
    services: ServiceItem[],
    quoteItems: QuoteItem[],
    lines: QuoteOrderLine[],
    opts?: LvPlanOptions,
): LvPlan => {
    const itemById = new Map((quoteItems || []).map((it) => [it.id, it]));
    let next = [...(services || [])];
    const steps: LvPlanStep[] = [];
    const newServiceIds: string[] = [];
    const missing: string[] = [];
    const packageBlocks: LvPlan['packageBlocks'] = [];
    const serviceIdByItemId: Record<string, string> = {};
    const missingTargets: LvPlan['missingTargets'] = [];
    const zuordnung = !!opts;
    let appended = 0;

    const indexOf = (id: string) => next.findIndex((s) => String(s.id) === id);

    /** Erste freie „eigene" id der Angebotsposition: ihre id, sonst abgeleitet. */
    const freeOwnId = (itemId: string): string => {
        if (indexOf(itemId) < 0) return itemId;
        for (let n = 2; ; n += 1) {
            const candidate = derivedServiceId(itemId, n);
            if (indexOf(candidate) < 0) return candidate;
        }
    };

    const append = (item: QuoteItem, mapped: ServiceItem, add: number, id: string, extra: Partial<LvPlanStep>) => {
        const lvPosition = opts?.appendLvPosition ? opts.appendLvPosition(appended) : mapped.lvPosition;
        appended += 1;
        const svc = id === mapped.id && lvPosition === mapped.lvPosition ? mapped : { ...mapped, id, lvPosition };
        next = [...next, svc];
        newServiceIds.push(id);
        if (zuordnung && !item.nep) serviceIdByItemId[item.id] = id;
        // Das Angebot hatte Bestandteile, der Mapper hat sie weggelassen
        // (Menge dieses Teils nicht 1): sichtbar machen, nicht still. Mit
        // ihnen faellt das Stundenkontingent (S1) — das sagt der Hinweis
        // dann auch.
        const verworfen = hatBestandteile(item) && !hatBestandteile(svc);
        const mitKontingent = verworfen && gueltigesKontingent(item.stundenKontingent) !== undefined;
        steps.push({
            itemId: item.id, kind: 'append', lvPosition: svc.lvPosition, title: svc.title, unit: svc.unit,
            from: 0, to: svc.quantityContracted, add,
            ...(zuordnung ? { serviceId: id } : {}),
            ...extra,
            ...(verworfen
                ? {
                      bestandteileHinweis: mitKontingent
                          ? 'Die Bestandteile und das Stundenkontingent aus dem Angebot werden nicht übernommen — es gibt sie nur bei Menge 1.'
                          : 'Die Bestandteile aus dem Angebot werden nicht übernommen — es gibt sie nur bei Menge 1.',
                  }
                : {}),
        });
    };

    for (const l of normalizeOrderLines(lines)) {
        const item = itemById.get(l.itemId);
        if (!item || item.nep || item.excluded) { missing.push(l.itemId); continue; }
        // Der Mapper bekommt die Menge DIESES Teils: an ihr entscheidet er,
        // ob die Bestandteile des Angebots mitkommen (nur bei Menge 1, E5 —
        // Begruendung an mapQuoteItemsToServiceItems).
        const mapped = mapQuoteItemsToServiceItems([{ ...item, quantity: l.quantity }])[0];

        // Zielposition suchen. Ohne Optionen ist der einzige Kandidat die
        // gleiche id — das bisherige Verhalten. Eine frühere Zuordnung ist
        // verbindlich: dann zählt die des Dialogs (der Vorschlag) nicht mehr,
        // sonst verteilte sich eine Angebotsposition auf zwei Projektpositionen.
        const prior = opts?.priorServiceIdByItemId?.[item.id];
        const candidates = [...new Set(
            [item.id, prior ?? opts?.mappedServiceIdByItemId?.[item.id]]
                .filter((id): id is string => !!id),
        )];
        let idx = -1;
        let firstBlock: string | null = null;
        let appendId: string | null = null;
        let lostTarget: string | null = null;
        for (const sid of candidates) {
            const at = indexOf(sid);
            if (at < 0) {
                // Eigene Position noch nicht im Projekt: anhängen bzw. weiter
                // zur Zuordnung. Eine abgeleitete eigene id (früherer Teil hat
                // sie vergeben, Eintragen steht noch aus) wird genau so angelegt.
                if (sid === item.id) continue;
                if (derivedIndex(item.id, sid) !== null) { appendId = sid; break; }
                lostTarget = sid;
                break;
            }
            const reason = opts?.increaseBlocker ? opts.increaseBlocker(next[at], item, l.quantity) : null;
            if (!reason) { idx = at; break; }
            firstBlock ??= reason;
        }

        if (lostTarget) {
            missingTargets.push({ itemId: item.id, serviceId: lostTarget });
            continue;
        }
        if (idx >= 0) {
            const svc = next[idx];
            const from = num(svc.quantityContracted);
            const to = qty3(from + l.quantity);
            const differs = Math.abs(num(svc.unitPrice) - mapped.unitPrice) > 0.005;
            // Bestandteile werden beim Erhoehen weder ergaenzt noch entfernt:
            // die Position steht schon im Projekt, ihre Definition gehoert dem
            // Projekt (Kopie statt Verweis). Eine Paketposition ueber Menge 1
            // hinaus ist GESPERRT (paketErhoehungGesperrt) — die Position
            // bleibt im Plan unveraendert, und `packageBlocks` sperrt das
            // Eintragen wie `missing`. Mit `increaseBlocker` prueft der das
            // Paket selbst (und macht daraus eine neue Position).
            const block = opts?.increaseBlocker ? null : paketErhoehungGesperrt(svc, to);
            if (block) {
                packageBlocks.push({ itemId: item.id, message: block });
            } else {
                next = next.map((s, i) => (i === idx ? { ...s, quantityContracted: to } : s));
            }
            if (zuordnung) serviceIdByItemId[item.id] = String(svc.id);
            steps.push({
                itemId: item.id, kind: 'increase', lvPosition: svc.lvPosition, title: svc.title, unit: svc.unit,
                from, to, add: l.quantity,
                ...(differs ? { priceDiffers: { project: num(svc.unitPrice), quote: mapped.unitPrice } } : {}),
                ...(block ? { packageBlock: block } : {}),
                ...(zuordnung ? { serviceId: String(svc.id) } : {}),
            });
            continue;
        }
        append(item, mapped, l.quantity, appendId ?? freeOwnId(item.id), firstBlock ? { forcedAppendReason: firstBlock } : {});
    }

    // NEP-Positionen (Eventualpositionen) gehen nur mit dem ERSTEN Teil ins
    // Projekt, als is_optional (Mapper). Steht die Position schon dort
    // (früherer Lauf), nichts doppelt.
    if (opts?.includeNep) {
        for (const item of activeQuoteItems(quoteItems || [])) {
            if (!item.nep || indexOf(item.id) >= 0) continue;
            const mapped = mapQuoteItemsToServiceItems([item])[0];
            append(item, mapped, num(item.quantity), item.id, { nep: true });
        }
    }
    return { services: next, steps, newServiceIds, missing, packageBlocks, serviceIdByItemId, missingTargets };
};

// ─── Auswahl im Dialog ──────────────────────────────────────────────────────

/** Auswahlzustand je Position: Haken und Mengentext (Zwischenstände wie "1," erlaubt). */
export type OrderSelection = Record<string, { on: boolean; text: string }>;

/**
 * Menge aus der Eingabe, deutsch oder englisch notiert ("1,5" / "1.5").
 * null = keine gültige Zahl (leer, Buchstaben, Zwischenstand).
 */
export const parseOrderQty = (text: string | null | undefined): number | null => {
    const t = String(text ?? '').trim().replace(/\s/g, '');
    if (!t) return null;
    const normalized = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t;
    if (!/^\d*\.?\d+$|^\d+\.$/.test(normalized)) return null;
    const n = Number(normalized);
    return Number.isFinite(n) ? n : null;
};

/**
 * Menge als EINGABETEXT: Dezimalkomma, aber OHNE Tausenderpunkt. "1.000"
 * liest parseOrderQty als 1 (Punkt ohne Komma = Dezimalpunkt) — eine
 * Vorbelegung mit Tausenderpunkt schickte ohne jede Eingabe die falsche Menge.
 */
export const formatQtyInput = (n: number): string =>
    num(n).toLocaleString('de-DE', { maximumFractionDigits: 3, useGrouping: false });

/** Vorbelegung: jede offene Position mit voller offener Menge, Haken nach `on`. */
export const initialOrderSelection = (q: Quote, on: boolean): OrderSelection => {
    const out: OrderSelection = {};
    for (const s of itemOrderStates(q).values()) {
        if (s.open > EPS) out[s.itemId] = { on, text: formatQtyInput(s.open) };
    }
    return out;
};

/** Zeilen aus der Auswahl: angehakt UND gültige Menge > 0. */
export const linesFromSelection = (sel: OrderSelection): QuoteOrderLine[] =>
    Object.entries(sel)
        .filter(([, v]) => v.on)
        .map(([itemId, v]) => ({ itemId, quantity: parseOrderQty(v.text) ?? 0 }))
        .filter((l) => l.quantity > 0);

/** Eine Zeile der Auswahltabelle. */
export interface OrderPickerRow {
    item: QuoteItem;
    state: ItemOrderState;
}

/** Abschnitte mit ihren noch offenen Positionen, Reihenfolge wie im Angebot. */
export const orderPickerGroups = (q: Quote): { id: string; title: string; rows: OrderPickerRow[] }[] => {
    const states = itemOrderStates(q);
    const titles = new Map((q.details?.sections || []).map((s) => [s.id, s.title]));
    const groups = new Map<string, { id: string; title: string; rows: OrderPickerRow[] }>();
    for (const item of orderableQuoteItems(q.items)) {
        const state = states.get(item.id);
        if (!state || state.open <= EPS) continue;
        const id = item.sectionId || '';
        let g = groups.get(id);
        if (!g) {
            g = { id, title: titles.get(id) || 'Ohne Abschnitt', rows: [] };
            groups.set(id, g);
        }
        g.rows.push({ item, state });
    }
    return [...groups.values()];
};

// ─── Anzeige ────────────────────────────────────────────────────────────────

export const formatQty = (n: number): string =>
    num(n).toLocaleString('de-DE', { maximumFractionDigits: 3 });

export const formatEur = (n: number): string =>
    `${num(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const formatDay = (iso: string): string => {
    const d = day(iso);
    return d ? `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}` : '';
};

/**
 * Text der Projektnotiz zu einem Teil. Hält im Projekt fest, was wann aus
 * welchem Angebot dazukam — auch für Koordinatoren, die das Angebot nicht
 * sehen dürfen (quotes ist admin-only).
 */
export const trancheNoteText = (q: Pick<Quote, 'quoteNumber' | 'items'>, t: QuoteOrderTranche): string => {
    const items = new Map((q.items || []).map((it) => [it.id, it]));
    const head = t.kind === 'decline'
        ? `Angebot ${q.quoteNumber}: Rest entfällt (Teil ${t.seq}, ${formatDay(t.orderDate)})`
        : `Teilbeauftragung Teil ${t.seq} aus Angebot ${q.quoteNumber}`
            + `${t.customerRef ? ` (Bestellung ${t.customerRef})` : ''} vom ${formatDay(t.orderDate)}`;
    const lines = t.lines.map((l) => {
        const it = items.get(l.itemId);
        return it
            ? `${it.lvPosition ? `${it.lvPosition} ` : ''}${it.title} ${t.kind === 'decline' ? '−' : '+'}${formatQty(l.quantity)} ${it.unit}`.trim()
            : `${l.itemId} ${formatQty(l.quantity)}`;
    });
    return `${head}: ${lines.join('; ')}. Netto ${formatEur(t.netAmount)}.${t.note ? ` ${t.note}` : ''}`;
};
